import React, { useEffect, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Row,
  Table,
  Col,
  Card,
  CardBody,
  Badge,
  Container,
} from "reactstrap"
import TableActionsDropdown from "components/Shared/TableActionsDropdown"
import TableTextCell from "components/Shared/TableTextCell"
import LtrText from "components/Shared/LtrText"

import { useLocation, useNavigate } from "react-router"
import DeleteModal from "components/Common/DeleteModal"
import Paginations from "components/Common/Paginations"
import Filters from "components/Common/Filters"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import {
  deleteAdminUser,
  getAllBranchs,
  getAllRoles,
  getAllUsers,
  updateAdminUserStatus,
} from "store/actions/usersAction"
import Switch from "components/Shared/Switch"
import isPermissionEnabled from "common/isPermissionEnabled"
import { getCrossModuleFilterPermissions } from "utils/crossModuleFilterPermissions"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import { showAlert } from "store/reducers/alertReducer"
import { commonItemPerPage, pageSizes } from "components/Common/constants"
import DownloadCSV from "components/Shared/DownloadCSV"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import TableShimmer from "components/Shared/TableShimmer"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import { useTranslation } from "react-i18next"
import ConfirmModal from "components/Common/ConfirmModal"

const Users = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Users List ")

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const rolesParam = searchParams.get("roles")
  const locationsParam = searchParams.get("locations")

  const dispatch = useDispatch()
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const navigate = useNavigate()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    roles: rolesParam
      ? rolesParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    locations: locationsParam
      ? locationsParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
  })
  const {
    loading,
    isLoading,
    isUpdating,
    totalCount,
    totalPages,
    currentPage,
    usersList,
    branchsList,
    rolesList,
  } = useSelector(state => state.Users)

  const [itemsPerPage, setItemsPerPage] = useState(commonItemPerPage)
  const [isApiCalled, setIsApiCalled] = useState(false)

  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const { canFilterByBranch, canFilterByRoles } = getCrossModuleFilterPermissions(
    userRole,
    accountType,
    accountScope
  )
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [statusUserId, setStatusUserId] = useState(null)
  const [statusToUpdate, setStatusToUpdate] = useState(null)

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const usersCategoryId = isSuperAdminSession ? 103 : 6
    const viewPermissionId = isSuperAdminSession
      ? "sa_users_1"
      : "usersPermission_1"
    const createPermissionId = isSuperAdminSession
      ? "sa_users_2"
      : "usersPermission_2"
    const editPermissionId = isSuperAdminSession
      ? "sa_users_3"
      : "usersPermission_3"
    const deletePermissionId = isSuperAdminSession
      ? "sa_users_4"
      : "usersPermission_4"
    const isPermissionList = isPermissionEnabled(
      userRole,
      usersCategoryId,
      viewPermissionId,
      accountType,
      accountScope
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      usersCategoryId,
      createPermissionId,
      accountType,
      accountScope
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      usersCategoryId,
      editPermissionId,
      accountType,
      accountScope
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      usersCategoryId,
      deletePermissionId,
      accountType,
      accountScope
    )
    if (!isPermissionList) {
      navigateToAccessibleRoute(navigate, { userRole, accountType })
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole, accountType, accountScope, isSuperAdminSession, navigate])

  useEffect(() => {
    fetchUsersData()
  }, [])

  useEffect(() => {
    if (canFilterByBranch) {
      dispatch(getAllBranchs())
    }
  }, [canFilterByBranch, dispatch])

  useEffect(() => {
    if (canFilterByRoles) {
      dispatch(getAllRoles())
    }
  }, [canFilterByRoles, dispatch])

  const fetchUsersData = () => {
    const { status, roles, locations, sortOrder } = selectedFilters
    const rolesIds = canFilterByRoles
      ? roles?.map(item => item.value) || []
      : []
    const locationsIds = canFilterByBranch
      ? locations?.map(item => item.value) || []
      : []

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: status, // 0 deactive, 1 active, 2 for all
      orderBy: sortOrder,
      keyword: searchTerm,
      role: rolesIds,
      location: locationsIds,
    }
    dispatch(getAllUsers(payload))
    setIsApiCalled(true)
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // 2 FOR DELETE
    }
    dispatch(deleteAdminUser({ ...payload, setShowDeleteModal }))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleConfirmStatusChange = () => {
    if (!statusUserId) return
    const payload = {
      id: statusUserId,
      status: statusToUpdate.toString(),
    }
    dispatch(updateAdminUserStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(err => {})
      .finally(() => {
        setShowStatusModal(false)
        setStatusUserId(null)
        setStatusToUpdate(null)
      })
  }

  const handleFetchUsers = (page = 1) => {
    const { status, roles, locations, sortOrder } = selectedFilters
    const rolesIds = roles?.map(item => item.value) || []
    const locationsIds = locations?.map(item => item.value) || []

    const searchParams = new URLSearchParams()
    searchTerm && searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    if (roles.length > 0) {
      const rolesString = roles
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("roles", rolesString)
    }

    if (locations.length > 0) {
      const locationsString = locations
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("locations", locationsString)
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status), // 0 deactive, 1 active, 2 for all
      orderBy: Number(sortOrder),
      role: rolesIds,
      keyword: searchTerm || "",
      location: locationsIds,
    }

    dispatch(getAllUsers(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/users?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchUsers(1) // Always search from page 1
  }

  const onPageChange = page => {
    handleFetchUsers(page)
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }
  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Active"), value: 1 },
        { label: t("Deactive"), value: 0 },
      ],
      placeholder: t("All"),
    },
    ...(canFilterByRoles
      ? [
          {
            name: "roles",
            type: "multiSelect",
            options: [
              ...(rolesList || []).map(role => ({
                label: language === "sa" ? role.nameAr || "-" : role.name,
                value: role._id,
              })),
            ],
            placeholder: t("Select Roles"),
          },
        ]
      : []),
    ...(canFilterByBranch
      ? [
          {
            name: "locations",
            type: "multiSelect",
            options: [
              ...(branchsList || []).map(branch => ({
                label: language === "sa" ? branch.nameAr : branch.name,
                value: branch._id,
              })),
            ],
            placeholder: t("Select Branch"),
          },
        ]
      : []),
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
  ]

  // useEffect(() => {
  //   if (isApiCalled) {
  //     const delayDebounceFn = setTimeout(() => {
  //       handleSearch()
  //     }, 500)

  //     return () => clearTimeout(delayDebounceFn)
  //   }
  // }, [searchTerm])

  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters])

  const handleReset = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2, // 0 deactive, 1 active, 2 for all
      orderBy: 1,
      keyword: "",
      location: [],
    }
    dispatch(getAllUsers(payload))
    setSelectedFilters({
      status: 2,
      roles: [],
      locations: [],
      sortOrder: 1,
    })
    setSearchTerm("")
    setItemsPerPage(commonItemPerPage)
  }

  const handleExcelDownload = async () => {
    const { status, roles, locations, sortOrder } = selectedFilters
    const rolesIds = roles?.map(item => item.value) || []
    const locationsIds = locations?.map(item => item.value) || []

    const payload = {
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      type: "USER",
      exportType: "xlsx",
      role: rolesIds,
      location: locationsIds,
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Users"))
    }
  }

  const exportToCSV = async () => {
    const { status, roles, locations, sortOrder } = selectedFilters
    const rolesIds = roles?.map(item => item.value) || []
    const locationsIds = locations?.map(item => item.value) || []

    const payload = {
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      type: "USER",
      exportType: "csv",
      role: rolesIds,
      location: locationsIds,
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Users"))
    }
  }

  const getUserActionItems = item => [
    ...(usersPermissions.edit
      ? [
          {
            key: "edit",
            content: (
              <>
                <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                {t("Edit")}
              </>
            ),
            onClick: () => navigate(`/users/edit?id=${item._id}`),
          },
        ]
      : []),
    {
      key: "view",
      content: (
        <>
          <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
          {t("View")}
        </>
      ),
      onClick: () => navigate(`/users/user-details?id=${item._id}`),
    },
    ...(usersPermissions.delete
      ? [
          {
            key: "delete",
            content: (
              <>
                <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                {t("Delete")}
              </>
            ),
            onClick: () => {
              setItemIdToDelete(item._id)
              setShowDeleteModal(true)
            },
          },
        ]
      : []),
  ]

  return (
    <>
      <div className="page-content users_page">
        <Container fluid>
          <Breadcrumbs />

          <Row>
            <Col xl={12}>
              <Card>
                <DataExportDropdown
                  // onDownloadPDF={handleDownloadPDF}
                  onDownloadCSV={exportToCSV}
                  onDownloadExcel={handleExcelDownload}
                  cardTitle={t("Users List")}
                  isAddBtn={loading ? false : usersPermissions.create}
                  addBtnText={t("Add New User")}
                  onAddBtnClick={() => navigate("/users/add")}
                  isExportData={loading ? false : true}
                />
                <CardBody>
                  {/* {!loading && ( */}
                  <Filters
                    filters={filters}
                    searchTerm={searchTerm}
                    handleSearchChange={handleSearchChange}
                    handleFilterChange={handleFilterChange}
                    handleSearch={handleSearch}
                    onReset={handleReset}
                    selectedFilters={selectedFilters}
                    isCustomPageSize={true}
                    pageSizes={pageSizes}
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={setItemsPerPage}
                    searchBy={t("Name, Email, Phone Number")}
                    colSize={4}
                  />
                  {/* )} */}

                  <div className="table-responsive">
                    {loading ? (
                      <TableShimmer row={7} col={7} />
                    ) : usersList.length === 0 ? (
                      <p className="text-center my-2">{t("No users found")}</p>
                    ) : (
                      <Table className="table table-nowrap mb-0" hover>
                        <thead className="table-light full_th">
                          <tr>
                            <th>{t("S. No.")}</th>
                            <th className="users-name-cell">{t("Name")}</th>
                            <th className="users-email-cell">{t("Email")}</th>
                            <th>{t("Phone Number")}</th>
                            <th className="users-role-cell">{t("Role")}</th>
                            <th>{t("Status")}</th>
                            <th className="align-middle">{t("Action")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usersList.map((item, index) => (
                            <tr
                              key={item._id}
                              onClick={() =>
                                navigate(`/users/user-details?id=${item._id}`)
                              }
                              className="cursor-pointer"
                            >
                              <th scope="row">
                                {(currentPage - 1) * 10 + index + 1}
                              </th>
                              {/* <td>{item.username}</td> */}
                              <td className="users-name-cell">
                                <TableTextCell
                                  text={`${item.firstName || ""} ${item.lastName || ""}`.trim()}
                                />
                              </td>
                              <td className="users-email-cell">
                                <TableTextCell text={item.email} />
                              </td>
                              <td>
                                {item.phoneNumber ? (
                                  <LtrText>
                                    <TableTextCell text={item.phoneNumber} />
                                  </LtrText>
                                ) : (
                                  <TableTextCell text={item.phoneNumber} />
                                )}
                              </td>
                              <td className="users-role-cell">
                                <TableTextCell text={item?.role?.name} />
                              </td>
                              <td>
                                {item.status === 1 ? (
                                  <Badge className="bg-success">
                                    {t("Active")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Deactive")}
                                  </Badge>
                                )}
                              </td>
                              <td className="align-middle">
                                <div className="d-inline-flex align-items-center gap-2">
                                  {usersPermissions.edit && (
                                    <Switch
                                      onClickHandler={e => {
                                        e.stopPropagation()
                                        setStatusUserId(item._id)
                                        setStatusToUpdate(
                                          item.status === 1 ? 0 : 1
                                        )
                                        setShowStatusModal(true)
                                      }}
                                      isChecked={item.status === 1}
                                      disabled={
                                        item._id === selectedId && isUpdating
                                      }
                                    />
                                  )}
                                  <TableActionsDropdown
                                    items={getUserActionItems(item)}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                  {!loading && usersList.length > 0 && (
                    <div className="mt-3">
                      <Paginations
                        data={usersList}
                        itemsPerPage={itemsPerPage}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("User"),
        })}
      />
      <ConfirmModal
        show={showStatusModal}
        isLoading={isUpdating}
        onDeleteClick={handleConfirmStatusChange}
        onCloseClick={() => setShowStatusModal(false)}
        buttonText={t("Yes")}
        bodyTitle={t("Are you sure you want to update the status?")}
      />
    </>
  )
}

export default Users
